// Starter Tape piano roll — pitch runs top to bottom (only the notes of the
// current scale are shown), time runs left to right in steps. Clicking an
// empty cell places a note; clicking a note removes it. Every row is labelled
// with both its letter and its scale number ("A · 1").
//
// With `onResize`, every note gets a handle on its right edge: drag it (or
// focus it and use the arrow keys) to change the note's length.
//
// The playhead is positioned by the parent through `playheadRef` (updated
// every animation frame without re-rendering the grid).

import React, { useState } from "react";
import { cn } from "../../lib/utils";

// "A4 (1)" for a note row, just "Kick" for a drum row.
const rowLabel = (row) =>
  row.degree ? `${row.name} (${row.degree})` : row.name;

export default function PianoRoll({
  rows,
  notes,
  steps,
  stepsPerBar,
  noteClass,
  onCellClick,
  onResize = null,
  playheadRef,
}) {
  const pct = (n) => `${(n / steps) * 100}%`;
  // While a note's right edge is being dragged: { pitch, step, length,
  // startX, startLength, stepWidth }. The song only changes on release.
  const [drag, setDrag] = useState(null);

  // Longest a note can be: up to the loop end or the next note on its row.
  const maxLength = (note) =>
    notes
      .filter((n) => n.pitch === note.pitch && n.step > note.step)
      .reduce((min, n) => Math.min(min, n.step), steps) - note.step;

  const startDrag = (e, note) => {
    const rowEl = e.currentTarget.closest("[data-roll-row]");
    if (!rowEl) return;
    e.preventDefault();
    e.stopPropagation();
    e.currentTarget.setPointerCapture(e.pointerId);
    setDrag({
      pitch: note.pitch,
      step: note.step,
      length: note.length,
      startX: e.clientX,
      startLength: note.length,
      stepWidth: rowEl.getBoundingClientRect().width / steps,
      max: maxLength(note),
    });
  };

  const moveDrag = (e) => {
    if (!drag) return;
    const delta = Math.round((e.clientX - drag.startX) / drag.stepWidth);
    const length = Math.max(1, Math.min(drag.max, drag.startLength + delta));
    if (length !== drag.length) setDrag({ ...drag, length });
  };

  const endDrag = () => {
    if (!drag) return;
    if (drag.length !== drag.startLength) {
      onResize(drag.pitch, drag.step, drag.length);
    }
    setDrag(null);
  };
  const cells = Array.from({ length: steps }, (_, i) => i);
  const beat = stepsPerBar / 4;

  return (
    <div className="overflow-x-auto rounded-xl border border-zinc-800 bg-zinc-900/60">
      {/* Long songs scroll sideways inside the roll, ~14px per step. */}
      <div
        className="flex"
        style={{ minWidth: Math.max(720, steps * 14 + 80) }}
      >
        {/* Row labels: letter · scale number */}
        <div className="w-20 shrink-0 border-r border-zinc-800">
          {rows.map((row) => (
            <div
              key={row.midi}
              className={cn(
                "flex h-8 items-center gap-1 border-b border-zinc-800/70 px-2 text-xs",
                row.isRoot ? "bg-zinc-800/80 text-zinc-100" : "text-zinc-400"
              )}
              title={row.name}
            >
              <span className="font-semibold">{row.letter}</span>
              {row.degree && (
                <>
                  <span className="text-zinc-500">·</span>
                  <span>{row.degree}</span>
                </>
              )}
              {row.isRoot && (
                <span className="ml-auto text-[10px] uppercase tracking-wide text-zinc-500">
                  home
                </span>
              )}
            </div>
          ))}
        </div>

        {/* Grid */}
        <div className="relative flex-1">
          {rows.map((row) => (
            <div
              key={row.midi}
              data-roll-row
              className={cn(
                "relative flex h-8 border-b border-zinc-800/70",
                row.isRoot && "bg-zinc-800/30"
              )}
            >
              {cells.map((step) => (
                <button
                  key={step}
                  type="button"
                  aria-label={`${rowLabel(row)}, step ${step + 1}`}
                  onClick={() => onCellClick(row.midi, step)}
                  className={cn(
                    "h-full flex-1 border-r hover:bg-zinc-700/40 focus:outline-none focus-visible:bg-zinc-700/60",
                    (step + 1) % stepsPerBar === 0
                      ? "border-zinc-600"
                      : (step + 1) % beat === 0
                        ? "border-zinc-800"
                        : "border-zinc-800/40"
                  )}
                />
              ))}
              {notes
                .filter((n) => n.pitch === row.midi)
                .map((n) => {
                  const dragging =
                    drag && drag.pitch === n.pitch && drag.step === n.step;
                  const length = dragging ? drag.length : n.length;
                  return (
                    <div
                      key={`${n.pitch}-${n.step}`}
                      className="absolute top-1 bottom-1"
                      style={{ left: pct(n.step), width: pct(length) }}
                    >
                      <button
                        type="button"
                        aria-label={`Remove ${rowLabel(row)} at step ${n.step + 1}`}
                        onClick={() => onCellClick(n.pitch, n.step)}
                        className={cn(
                          "h-full w-full rounded-md border shadow-sm transition-opacity hover:opacity-80",
                          noteClass,
                          dragging && "opacity-80"
                        )}
                      />
                      {onResize && (
                        <span
                          role="slider"
                          tabIndex={0}
                          aria-label={`Length of ${rowLabel(row)} at step ${n.step + 1}`}
                          aria-valuemin={1}
                          aria-valuemax={maxLength(n)}
                          aria-valuenow={length}
                          onPointerDown={(e) => startDrag(e, n)}
                          onPointerMove={moveDrag}
                          onPointerUp={endDrag}
                          onPointerCancel={() => setDrag(null)}
                          onKeyDown={(e) => {
                            const delta =
                              e.key === "ArrowRight"
                                ? 1
                                : e.key === "ArrowLeft"
                                  ? -1
                                  : 0;
                            if (!delta) return;
                            e.preventDefault();
                            onResize(n.pitch, n.step, n.length + delta);
                          }}
                          title="Drag to change the length"
                          className="absolute top-0 right-0 bottom-0 w-3 cursor-ew-resize touch-none rounded-r-md hover:bg-white/30 focus:outline-none focus-visible:bg-white/40"
                        />
                      )}
                    </div>
                  );
                })}
            </div>
          ))}
          <div
            ref={playheadRef}
            className="pointer-events-none absolute top-0 bottom-0 w-0.5 bg-zinc-100/80"
            style={{ display: "none" }}
          />
        </div>
      </div>
    </div>
  );
}
